import { useState } from 'react';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { installAvailable, triggerInstall, isStandalone, isIOS } from '../lib/pwaInstall';
import { isOffensive } from '../lib/badwords';
import { restoreIdentity } from '../lib/identity';
import { api } from '../api/client';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px;
  @media (min-width: 600px) {
    align-items: center;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 18px;
  padding: 22px;
  max-height: 92vh;
  overflow-y: auto;

  h1 { font-size: 1.6rem; margin: 0 0 6px; }
  .sub { color: ${({ theme }) => theme.colors.textMuted}; font-size: 1.05rem; line-height: 1.5; }
  ul { list-style: none; padding: 0; margin: 16px 0; }
  li { padding: 6px 0; font-size: 1.05rem; display: flex; gap: 10px; align-items: center; }
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-left: 4px solid ${({ theme }) => theme.colors.gold};
  border-radius: 10px;
  padding: 14px;
  margin: 16px 0;
  font-size: 1.05rem;
  line-height: 1.5;
  strong { color: ${({ theme }) => theme.colors.gold}; }
`;

const InstallCTA = styled.div`
  font-size: 1.05rem;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const NickField = styled.div`
  margin-bottom: 14px;
  label { display: block; font-weight: 700; margin-bottom: 6px; }
  input {
    width: 100%;
    box-sizing: border-box;
    font-size: 1.1rem;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Hint = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 8px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  width: 100%;
  border: 1.5px solid ${({ theme, $primary }) => ($primary ? theme.colors.accent : theme.colors.borderStrong)};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $primary }) => ($primary ? theme.colors.accentText : theme.colors.text)};
  border-radius: 12px;
  padding: 13px;
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 8px;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export function Welcome() {
  const seenWelcome = useStore((s) => s.seenWelcome);
  const setSeenWelcome = useStore((s) => s.setSeenWelcome);
  const setNick = useStore((s) => s.setNick);
  const setIntroPending = useStore((s) => s.setIntroPending);
  const [iosHelp, setIosHelp] = useState(false);
  const [nick, setNickInput] = useState('');
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [restoreMsg, setRestoreMsg] = useState('');

  // No mostrar si ya lo vio o si ya está instalada (corriendo como app).
  if (seenWelcome || isStandalone()) return null;

  const nickBad = nick.trim().length >= 2 && isOffensive(nick);
  const nickOk = nick.trim().length >= 2 && !nickBad;
  const saveNick = () => {
    if (nickOk) setNick(nick.trim());
  };

  const handleInstall = async () => {
    saveNick();
    if (installAvailable()) {
      const r = await triggerInstall();
      if (r === 'accepted') {
        setIntroPending(true);
        setSeenWelcome();
      }
      return;
    }
    if (isIOS()) {
      setIosHelp(true);
      return;
    }
    // Navegadores que no exponen el prompt: guía genérica.
    setIosHelp(true);
  };

  const enter = () => {
    saveNick();
    setIntroPending(true);
    setSeenWelcome();
  };

  // Restaurar identidad ya existente (otro navegador / la app instalada) pegando la clave.
  // Recupera la pubkey → vuelven sus pronósticos/torneos; trae el nick del server y recarga.
  const restore = async () => {
    try {
      const id = restoreIdentity(keyInput);
      const d = await api.timbaPlayer(id.pub).catch(() => null);
      if (d?.nick) setNick(d.nick);
      setSeenWelcome();
      window.location.reload();
    } catch {
      setRestoreMsg('No pude restaurar. Revisá las 24 palabras (o la clave).');
    }
  };

  return (
    <Overlay>
      <Card>
        <h1>¡Bienvenido a VamoVamo!</h1>
        <div className="sub">
          Tu compañero para vivir el <strong>Mundial 2026</strong>. Seguí cada partido y armá tu
          propia historia del torneo.
        </div>

        <ul>
          <li>Calendario y resultados al día</li>
          <li>Zonas, posiciones y goleadores</li>
          <li>Simulá la llave: elegí quién pasa y coroná al campeón</li>
          <li>Sedes, estadios y estadísticas</li>
        </ul>

        <NickField>
          <label htmlFor="nick">Elegí un apodo</label>
          <input
            id="nick"
            type="text"
            maxLength={24}
            value={nick}
            onChange={(e) => setNickInput(e.target.value)}
            placeholder="Tu apodo"
            autoComplete="nickname"
          />
          {nickBad && <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '6px 0 0' }}>Elegí otro apodo, por favor.</p>}
        </NickField>

        <IdNote>
          No pongas datos reales, no nos interesan. Vas a crear una <strong>identidad soberana y
          privada</strong>. Si querés usarla en otro dispositivo, hacé el <strong>backup de seguridad</strong>
          {' '}(Preferencias → Tu identidad): te va a dar <strong>24 palabras</strong>, tu clave de recuperación.
        </IdNote>

        <Btn $primary onClick={enter} disabled={!nickOk}>Jugar</Btn>

        <InstallCTA>
          <strong>Opcional:</strong> agregala a tu pantalla de inicio y tené el Mundial siempre a
          mano, como una app — abre al instante y funciona aún con poca señal. (No hace falta para jugar.)
        </InstallCTA>

        {iosHelp && (
          <Note>
            {isIOS() ? (
              <>En iPhone/iPad: tocá <strong>Compartir</strong> ⬆️ y elegí{' '}
              <strong>"Agregar a inicio"</strong>.</>
            ) : (
              <>Abrí el menú del navegador (⋮) y elegí{' '}
              <strong>"Agregar a pantalla de inicio"</strong>.</>
            )}
          </Note>
        )}

        <Btn onClick={handleInstall} disabled={!nickOk}>Agregar al inicio</Btn>
        {!nickOk && <Hint>Elegí un apodo para continuar.</Hint>}

        <Restore>
          {!restoreOpen ? (
            <button type="button" className="link" onClick={() => setRestoreOpen(true)}>
              ¿Ya jugás en VamoVamo? Restaurá tu identidad
            </button>
          ) : (
            <>
              <p className="lbl">Pegá tus <strong>24 palabras</strong> (o la clave) del backup de tu otro dispositivo, para no duplicar tu usuario:</p>
              <div className="row">
                <input
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="tus 24 palabras (o la clave)"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button type="button" onClick={restore} disabled={keyInput.trim().length < 8}>Restaurar</button>
              </div>
              {restoreMsg && <p className="err">{restoreMsg}</p>}
            </>
          )}
        </Restore>
      </Card>
    </Overlay>
  );
}

const Restore = styled.div`
  margin-top: 14px;
  text-align: center;
  .link {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textMuted};
    text-decoration: underline;
    font-size: 0.82rem;
    cursor: pointer;
  }
  .lbl { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; margin: 0 0 8px; line-height: 1.4; text-align: left; }
  .row { display: flex; gap: 8px; }
  .row input {
    flex: 1;
    min-width: 0;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 0.85rem;
  }
  .row button {
    flex: 0 0 auto;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
    border-radius: 10px;
    padding: 0 14px;
    font-weight: 700;
  }
  .row button:disabled { opacity: 0.5; }
  .err { color: #ff6b6b; font-size: 0.8rem; margin: 8px 0 0; }
`;

const IdNote = styled.p`
  margin: 0 0 14px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 10px;
  padding: 12px;
  strong { color: ${({ theme }) => theme.colors.text}; }
`;
